import React from 'react';
import { Link } from 'react-router-dom';
import LandingNav    from '../components/Landing/LandingNav';
import LandingFooter from '../components/Landing/LandingFooter';

const EFFECTIVE_DATE  = 'March 6, 2026';
const LAST_UPDATED    = 'March 6, 2026';
const COMPANY         = 'Eternium LLC';
const PRODUCT         = 'Helix';
const CONTACT_EMAIL   = 'legal@helix.ai';
const SUPPORT_EMAIL   = 'support@helix.ai';

// ── Shared typography helpers ─────────────────────────────────────────────────
const H2 = ({ id, children }) => (
    <h2 id={id} className="text-xl font-heading font-bold text-white mt-10 mb-4 scroll-mt-24">
        {children}
    </h2>
);
const H3 = ({ children }) => (
    <h3 className="text-base font-bold text-white/90 mt-6 mb-2">{children}</h3>
);
const P  = ({ children }) => (
    <p className="text-[var(--text-secondary)] leading-relaxed mb-4">{children}</p>
);
const UL = ({ children }) => (
    <ul className="list-disc list-inside text-[var(--text-secondary)] leading-relaxed mb-4 space-y-1.5 pl-2">
        {children}
    </ul>
);
const LI = ({ children }) => <li>{children}</li>;

// ─────────────────────────────────────────────────────────────────────────────
export default function Terms() {
    return (
        <div className="min-h-screen bg-[var(--bg-dark)] text-white">
            <LandingNav />

            {/* Hero */}
            <div className="pt-32 pb-10 px-6 border-b border-white/6">
                <div className="max-w-3xl mx-auto">
                    <span className="text-xs font-bold uppercase tracking-widest text-[var(--primary-color)] mb-3 block">
                        Legal
                    </span>
                    <h1 className="text-4xl font-heading font-black text-white mb-3">Terms of Service</h1>
                    <p className="text-sm text-[var(--text-secondary)]">
                        Effective: {EFFECTIVE_DATE} &nbsp;·&nbsp; Last updated: {LAST_UPDATED}
                    </p>
                </div>
            </div>

            {/* Body */}
            <div className="max-w-3xl mx-auto px-6 py-12">

                {/* Intro */}
                <P>
                    Please read these Terms of Service ("Terms") carefully before using the {PRODUCT}
                    platform operated by {COMPANY} ("we," "us," or "our"). By accessing or using the
                    Service you agree to be bound by these Terms. If you do not agree, do not use the Service.
                </P>

                {/* ── 1. Definitions ─────────────────────────────────────────────────── */}
                <H2 id="definitions">1. Definitions</H2>
                <UL>
                    <LI><strong className="text-white">"Service"</strong> — the {PRODUCT} web application, APIs, and all associated features provided by {COMPANY}.</LI>
                    <LI><strong className="text-white">"User," "you"</strong> — any individual or entity that creates an account or accesses the Service.</LI>
                    <LI><strong className="text-white">"Content"</strong> — text, images, video, audio, or any other material uploaded, generated, or stored through the Service.</LI>
                    <LI><strong className="text-white">"Credits"</strong> — the digital currency used within the Service to consume AI generation features.</LI>
                    <LI><strong className="text-white">"Subscription"</strong> — a recurring billing arrangement for a specific tier of Service access.</LI>
                    <LI><strong className="text-white">"AI Generations"</strong> — images, video clips, or other media produced by third-party AI models through the Service.</LI>
                </UL>

                {/* ── 2. Eligibility ──────────────────────────────────────────────────── */}
                <H2 id="eligibility">2. Eligibility</H2>
                <P>
                    You must be at least 18 years of age to use the Service. By using the Service you
                    represent and warrant that you meet this requirement and have the legal capacity to
                    enter into these Terms.
                </P>

                {/* ── 3. Accounts ─────────────────────────────────────────────────────── */}
                <H2 id="accounts">3. Accounts</H2>
                <P>
                    You are responsible for maintaining the confidentiality of your account credentials and
                    for all activity that occurs under your account. Notify us immediately at{' '}
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--primary-color)] hover:underline">{SUPPORT_EMAIL}</a>{' '}
                    if you suspect unauthorized use. We reserve the right to suspend or terminate accounts
                    that violate these Terms.
                </P>

                {/* ── 4. Subscription Plans & Billing ─────────────────────────────────── */}
                <H2 id="billing">4. Subscription Plans &amp; Billing</H2>

                <H3>4.1 Plans</H3>
                <P>
                    {PRODUCT} offers Free, Creator, Pro, and Enterprise subscription plans. Each plan
                    includes a defined set of features, storage, and bundled AI generation allotments.
                    Plan details, pricing, and included allotments are described at{' '}
                    <Link to="/#pricing" className="text-[var(--primary-color)] hover:underline">helix.ai/#pricing</Link>{' '}
                    and in your account dashboard.
                </P>

                <H3>4.2 Billing Cycle</H3>
                <P>
                    Paid subscriptions are billed monthly or annually in advance. All fees are
                    non-refundable except as required by applicable law or as stated in our 7-day
                    money-back guarantee for first-time subscribers. Prices are in USD and may be
                    subject to applicable taxes.
                </P>

                <H3>4.3 Upgrades &amp; Downgrades</H3>
                <P>
                    You may upgrade your plan at any time; the new rate applies immediately with a
                    prorated credit for unused days. Downgrades take effect at the end of the current
                    billing period.
                </P>

                <H3>4.4 Cancellation</H3>
                <P>
                    You may cancel your subscription at any time via your Account page or by contacting
                    support. Access continues through the end of the paid billing period. We do not
                    provide refunds for partial months except as required by law.
                </P>

                {/* ── 5. Credits ──────────────────────────────────────────────────────── */}
                <H2 id="credits">5. Credits</H2>
                <P>
                    Credits are a non-refundable, non-transferable in-platform currency used to pay for
                    AI generation beyond bundled subscription allotments. Purchased credits do not expire.
                    Credits have no cash value and cannot be redeemed for money. Upon account termination,
                    all unused credits are forfeited.
                </P>

                {/* ── 6. Fair Use Policy ──────────────────────────────────────────────── */}
                <H2 id="fair-use">6. Fair Use Policy — "Unlimited" Generation</H2>

                {/* Highlighted box */}
                <div className="rounded-2xl border border-[var(--primary-color)]/30 bg-[rgba(24,181,240,0.04)] p-6 mb-6">
                    <p className="text-xs font-bold uppercase tracking-widest text-[var(--primary-color)] mb-3">
                        Important — Read This Section Carefully
                    </p>
                    <p className="text-[var(--text-secondary)] leading-relaxed mb-4">
                        The <strong className="text-white">"Unlimited AI Generations"</strong> feature
                        available on the <strong className="text-white">Pro</strong> and{' '}
                        <strong className="text-white">Enterprise</strong> plans is subject to this
                        Fair Use Policy to ensure platform stability and fair access for all users.
                    </p>

                    <p className="text-sm font-bold text-white mb-2">Monthly generation thresholds (per account):</p>
                    <div className="overflow-hidden rounded-xl border border-white/8">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-white/8 bg-white/[0.03]">
                                    <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[var(--text-secondary)]">Plan</th>
                                    <th className="text-center px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[var(--text-secondary)]">Images / mo</th>
                                    <th className="text-center px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[var(--text-secondary)]">Video Clips / mo</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-b border-white/5">
                                    <td className="px-4 py-2.5 text-white font-medium">Pro</td>
                                    <td className="px-4 py-2.5 text-center text-[var(--text-secondary)]">1,000</td>
                                    <td className="px-4 py-2.5 text-center text-[var(--text-secondary)]">100</td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-2.5 text-white font-medium">Enterprise</td>
                                    <td className="px-4 py-2.5 text-center text-[var(--text-secondary)]">5,000 / seat</td>
                                    <td className="px-4 py-2.5 text-center text-[var(--text-secondary)]">500 / seat</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <p className="text-xs text-[var(--text-secondary)] mt-4 leading-relaxed">
                        Generations within these thresholds do not consume credits. Generations beyond
                        these thresholds will draw from the account's credit balance. If insufficient
                        credits are available, additional credits must be purchased to continue
                        generating. Thresholds reset on the first day of each calendar billing cycle.
                    </p>
                    <p className="text-xs text-[var(--text-secondary)] mt-2 leading-relaxed">
                        {COMPANY} reserves the right to adjust these thresholds with 30 days' written
                        notice. Enterprise plans may negotiate custom thresholds via a separate agreement.
                    </p>
                </div>

                <P>
                    Users who consistently and materially exceed these thresholds in ways that degrade
                    service for other users may be contacted by {COMPANY} to discuss an appropriate
                    usage plan, including custom Enterprise pricing.
                </P>

                {/* ── 7. Acceptable Use ────────────────────────────────────────────────── */}
                <H2 id="acceptable-use">7. Acceptable Use</H2>
                <P>You agree not to use the Service to:</P>
                <UL>
                    <LI>Generate, upload, or distribute content that is illegal, harmful, abusive, harassing, defamatory, obscene, or infringing on any third-party rights.</LI>
                    <LI>Produce CSAM or any content that sexually exploits minors.</LI>
                    <LI>Impersonate any person or entity or misrepresent your affiliation.</LI>
                    <LI>Attempt to circumvent rate limits, usage caps, or authentication mechanisms.</LI>
                    <LI>Reverse-engineer, decompile, or extract the underlying AI models.</LI>
                    <LI>Resell, sublicense, or white-label the Service without express written permission from {COMPANY}.</LI>
                    <LI>Use automated scripts or bots to access the Service in a manner that exceeds normal human usage.</LI>
                </UL>
                <P>
                    We reserve the right to immediately suspend or terminate accounts in violation of
                    this section without refund.
                </P>

                {/* ── 8. AI-Generated Content & IP ────────────────────────────────────── */}
                <H2 id="ip">8. Intellectual Property &amp; AI-Generated Content</H2>

                <H3>8.1 Your Content</H3>
                <P>
                    You retain ownership of any original content you upload to the Service. By uploading
                    content you grant {COMPANY} a limited, non-exclusive, royalty-free license to store
                    and process that content solely to provide the Service to you.
                </P>

                <H3>8.2 AI-Generated Outputs</H3>
                <P>
                    Subject to your compliance with these Terms, {COMPANY} assigns to you all right,
                    title, and interest {COMPANY} may have in AI-generated outputs produced by your
                    account. You acknowledge that (a) AI outputs may not be copyrightable in all
                    jurisdictions under current law, and (b) similar outputs may be generated for other
                    users. {COMPANY} makes no warranty regarding the uniqueness of AI-generated content.
                </P>

                <H3>8.3 Helix Platform IP</H3>
                <P>
                    The {PRODUCT} platform, including its software, design, trademarks, and branding,
                    is the exclusive property of {COMPANY}. Nothing in these Terms grants you any right
                    to use our trademarks or branding without prior written consent.
                </P>

                {/* ── 9. Third-Party Services ─────────────────────────────────────────── */}
                <H2 id="third-party">9. Third-Party Services</H2>
                <P>
                    The Service integrates with third-party providers including but not limited to:
                    payment processing (Stripe), AI model providers (Eternium API), cloud storage
                    (Cloudflare R2), and authentication services. Your use of these integrations is also
                    subject to those providers' terms and privacy policies. {COMPANY} is not responsible
                    for the availability, accuracy, or conduct of third-party services.
                </P>

                {/* ── 10. Disclaimer of Warranties ────────────────────────────────────── */}
                <H2 id="disclaimer">10. Disclaimer of Warranties</H2>
                <P>
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                    EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY,
                    FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT
                    THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL
                    COMPONENTS. AI-GENERATED CONTENT MAY CONTAIN INACCURACIES AND SHOULD NOT BE RELIED
                    UPON AS FACTUAL OR PROFESSIONAL ADVICE.
                </P>

                {/* ── 11. Limitation of Liability ─────────────────────────────────────── */}
                <H2 id="liability">11. Limitation of Liability</H2>
                <P>
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL {COMPANY.toUpperCase()},
                    ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                    SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR
                    GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE, EVEN IF
                    ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL AGGREGATE LIABILITY SHALL NOT
                    EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE
                    CLAIM OR (B) $100 USD.
                </P>

                {/* ── 12. Indemnification ─────────────────────────────────────────────── */}
                <H2 id="indemnification">12. Indemnification</H2>
                <P>
                    You agree to indemnify and hold harmless {COMPANY} and its officers, directors,
                    employees, and agents from any claims, damages, losses, liabilities, and expenses
                    (including attorneys' fees) arising out of your use of the Service, your Content,
                    or your violation of these Terms.
                </P>

                {/* ── 13. Termination ─────────────────────────────────────────────────── */}
                <H2 id="termination">13. Termination</H2>
                <P>
                    We may suspend or terminate your access to the Service at any time, with or without
                    cause, with or without notice, effective immediately. Upon termination, all licenses
                    granted to you cease, and you remain responsible for any outstanding fees. Sections
                    8, 10, 11, 12, and 14 survive termination.
                </P>

                {/* ── 14. Governing Law ────────────────────────────────────────────────── */}
                <H2 id="governing-law">14. Governing Law &amp; Dispute Resolution</H2>
                <P>
                    These Terms are governed by the laws of the State of Delaware, United States,
                    without regard to its conflict-of-law provisions. Any dispute arising under these
                    Terms shall be resolved by binding arbitration administered by the American
                    Arbitration Association under its Commercial Arbitration Rules, with proceedings
                    conducted in English. You waive any right to participate in a class-action lawsuit
                    or class-wide arbitration.
                </P>

                {/* ── 15. Changes ─────────────────────────────────────────────────────── */}
                <H2 id="changes">15. Changes to These Terms</H2>
                <P>
                    We reserve the right to modify these Terms at any time. Material changes will be
                    communicated via email or a prominent in-app notice at least 14 days before they
                    take effect. Continued use of the Service after the effective date constitutes
                    acceptance of the revised Terms. Fair Use thresholds (Section 6) will receive
                    30 days' notice before any reduction.
                </P>

                {/* ── 16. Contact ─────────────────────────────────────────────────────── */}
                <H2 id="contact">16. Contact</H2>
                <P>
                    Questions about these Terms? Contact us at{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--primary-color)] hover:underline">
                        {CONTACT_EMAIL}
                    </a>{' '}
                    or write to:
                </P>
                <div className="glass-panel rounded-xl p-4 text-sm text-[var(--text-secondary)] mb-8">
                    {COMPANY}<br />
                    Legal Department<br />
                    legal@helix.ai
                </div>

                {/* Links to other legal docs */}
                <div className="border-t border-white/6 pt-8 flex flex-wrap gap-4 text-sm">
                    <Link to="/privacy" className="text-[var(--primary-color)] hover:underline">Privacy Policy</Link>
                    <Link to="/#pricing" className="text-[var(--text-secondary)] hover:text-white transition-colors">Pricing</Link>
                    <Link to="/" className="text-[var(--text-secondary)] hover:text-white transition-colors">← Back to Helix</Link>
                </div>
            </div>

            <LandingFooter />
        </div>
    );
}
